import React, { useEffect } from 'react';
import {
  List,
  Datagrid,
  TextField,
  Pagination,
  FunctionField,
  useRecordContext,
  useResourceContext,
  useListContext,
  useStore,
  ListBase,
  usePermissions,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import ClientSplitRulesFilters from './ClientSplitRulesFilters';
import {
  CleanupParams,
  ClientBillSplitParams,
  ClientCombineParams,
  RuleRecord,
  TpaTransitionParams,
} from '../utils/DataTypes';
import { formatDate, formatSplitTypes } from './clientSplitHelpers';
import DeactivateRuleButton from './DeactivateRuleButton';
import SqlQueryBlock from './SQLQueryBlock';

const RuleDetails = () => {
  const record = useRecordContext<RuleRecord>();
  if (!record) return null;

  const params = record.extract_params || {};

  return (
    <Box sx={{ pl: '48px', py: 2.5, pr: 4 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#666', mb: 1 }}>
        Applied Logic
      </Typography>

      <SqlQueryBlock query={params.fullSqlQuery} />
    </Box>
  );
};

const CombineDetails = ({ params }: { params: ClientCombineParams }) => (
  <Box sx={{ pl: '48px', py: 2 }}>
    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <Box>
        <strong>Primary Client</strong>
        <div>
          {params.primaryClient.client_name} ({params.primaryClient.client_number})
        </div>
      </Box>

      <Box>
        <strong>Clients to Combine</strong>
        <div>
          {params.combinedClients.map((c, index) => (
            <span key={c.client_number}>
              {c.client_name} ({c.client_number}){index < params.combinedClients.length - 1 ? ', ' : ''}
            </span>
          ))}
        </div>
      </Box>
    </Box>
  </Box>
);

const TpaDetails = ({ params }: { params: TpaTransitionParams }) => (
  <Box sx={{ pl: '48px', py: 2 }}>
    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <Box>
        <strong>Client</strong>
        <div>
          {params.clientName} ({params.clientNumber})
        </div>
      </Box>

      <Box>
        <strong>TPA Change</strong>
        <div>
          {params.oldTpaName} ({params.oldTpaBillingNumber}) → {params.newTpaName} ({params.newTpaBillingNumber})
        </div>
      </Box>
    </Box>
  </Box>
);

const SplitDetails = ({ params }: { params: ClientBillSplitParams }) => {
  const isPharmacy = params.fieldToSplit?.toLowerCase() === 'pharmacy';

  return (
    <Box sx={{ pl: '48px', py: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {/* Column 1 */}
        <Box>
          <strong>Field to Split</strong>
          <div>{params.fieldToSplit}</div>

          {!isPharmacy && (
            <Box mt={2}>
              <strong>Source Field</strong>
              <div>{params.sourceField}</div>
            </Box>
          )}
        </Box>

        {/* Column 2 */}
        <Box>
          <strong>New Client</strong>
          <div>
            {params.newClientName} ({params.newClientNumber})
          </div>

          <Box mt={2}>
            <strong>{!isPharmacy && 'Match Value'}</strong>
            <div>{!isPharmacy && params.matchValue}</div>
          </Box>
        </Box>

        <Box>
          {isPharmacy ? (
            <>
              <strong>Values</strong>
              <div>{(params.identifierValues ?? []).join(', ')}</div>
            </>
          ) : (
            <>
              <strong>Trim Instructions</strong>
              <div>
                Left: {params.leftTrim} &nbsp; Right: {params.rightTrim}
              </div>

              <Box mt={2}>
                <strong>Minimum Length</strong>
                <div>{params.conditionLength}</div>
              </Box>
            </>
          )}
        </Box>
        <Box>
          <strong>{isPharmacy && 'Identifiers'}</strong>
          <div>{isPharmacy && params.identifierType}</div>
        </Box>
      </Box>
    </Box>
  );
};

const CleanupDetails = ({ params }: { params: CleanupParams }) => (
  <Box sx={{ pl: '48px', py: 2 }}>
    <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <Box>
        <strong>Field to Update</strong>
        <div>{params.fieldToUpdate}</div>
      </Box>

      <Box>
        <strong>Source Field</strong>
        <div>{params.sourceField}</div>
      </Box>

      <Box>
        <strong>Trim Instructions</strong>
        <div>
          Left Trim: {params.leftTrim}, Right Trim: {params.rightTrim}
        </div>
      </Box>

      <Box>
        <strong>Minimum Length</strong>
        <div>{params.conditionLength}</div>
      </Box>
    </Box>
  </Box>
);

const ResetExpandedRowsOnPageChange = () => {
  const resource = useResourceContext();
  const { page, perPage } = useListContext();

  const [expandedIds, setExpandedIds] = useStore(`${resource}.datagrid.expanded`, []);

  useEffect(() => {
    if (expandedIds.length > 0) {
      setExpandedIds([]);
    }
  }, [page, perPage, setExpandedIds]);

  return null;
};

const AutoClearFilters = () => {
  const { setFilters, setSort } = useListContext();
  useEffect(() => {
    setFilters({});
    setSort({ field: 'billing_client_number', order: 'ASC' });
  }, []);
  return null;
};

const ClientSplitRulesList = () => {
  const { permissions } = usePermissions();
  return (
    <ListBase
      resource="ClientSplitRulesList"
      perPage={10}
      sort={{ field: 'billing_client_number', order: 'ASC' }}
      storeKey={false}
    >
      <ClientSplitRulesFilters />
      <AutoClearFilters />
      <List pagination={<Pagination />} actions={false} sx={{ '& .RaList-main': { p: 0 } }}>
        <ResetExpandedRowsOnPageChange />
        <Datagrid
          bulkActionButtons={false}
          expand={<RuleDetails />}
          sx={{
            '& .MuiTableCell-body': {
              fontSize: '0.85rem',
            },
            '& .MuiTableCell-head': {
              whiteSpace: 'nowrap',
            },
          }}
        >
          <TextField source="client_number" label="Client #" sortable={true} />
          <FunctionField
            label="Client Name"
            sortable={true}
            sortBy="client_name"
            render={(record: any) => (
              <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: 'inherit' }} title={record.client_name}>
                {record.client_name}
              </Typography>
            )}
          />
          <FunctionField label="Split Type" render={(record: any) => formatSplitTypes(record.split_type)} />
          <TextField source="billing_client_number" label="Billing Client #" sortable={true} />
          <FunctionField
            label="Billing Client Name"
            sortable={true}
            sortBy="billing_client_name"
            render={(record: any) => (
              <Typography
                variant="body2"
                noWrap
                sx={{ maxWidth: 200, fontSize: 'inherit' }}
                title={record.billing_client_name}
              >
                {record.billing_client_name}
              </Typography>
            )}
          />
          <FunctionField
            label="Source"
            render={(record: any) => {
              const isManual = record.extract_params?.ruleEntryMode === 'custom_sql';
              return isManual ? 'Manual' : 'UI';
            }}
          />
          <FunctionField
            label="Effective Date"
            render={(record: any) => formatDate(record.effective_start_dt)}
            sortable={true}
          />

          <FunctionField label="Created Date" render={(record: any) => formatDate(record.created_dt)} sortable={true} />

          <TextField source="created_by" label="Created By" sortable={true} />
          {permissions.adminPermission && <FunctionField label="Actions" render={() => <DeactivateRuleButton />} />}
        </Datagrid>
      </List>
    </ListBase>
  );
};

export default ClientSplitRulesList;
